/**
 * "Данные" page (Система → Данные) — superuser-only ClearML dataset tooling.
 *
 * Tab 1: dataset hierarchy (browse / lock / download a copy to the server).
 * Tab 2: annotation editor for a downloaded ResNet `table-occupancy-crops`
 *        dataset (keyboard-driven multiclass labeling). [built in a later phase]
 *
 * Route is registered under <SuperuserGuard> in routes.tsx.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { getActiveDownloads, getDownload, datasetKind } from '@/api/datasets';
import DatasetHierarchy, { type PoolDomain } from './datasets/DatasetHierarchy';
import CropAnnotator from './datasets/CropAnnotator';
import AudioTranscriber from './datasets/AudioTranscriber';
import ShelfPercentageAnnotator from './datasets/ShelfPercentageAnnotator';
import PooledTableAnnotator from './datasets/PooledTableAnnotator';
import CollectionFilter from './datasets/CollectionFilter';
import PooledShelfAnnotator from './datasets/PooledShelfAnnotator';
import BoxAnnotator from './datasets/BoxAnnotator';
import PeopleAnnotator from './datasets/PeopleAnnotator';
import PooledAudioAnnotator from './datasets/PooledAudioAnnotator';
import ConversationMarkupTool from './datasets/ConversationMarkupTool';
import SegmentStream from './datasets/SegmentStream';

/** Picks the annotator by the downloaded dataset's kind (audio / shelf % / table crops). */
function AnnotatorSwitch({ downloadId, onClose }: { downloadId: number; onClose: () => void }) {
  const { t } = useT();
  const { data: dd, isLoading } = useSWR(['dl-kind', downloadId], () => getDownload(downloadId).then(r => r.data));
  if (isLoading || !dd) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  const kind = datasetKind(dd.dataset_name);
  if (kind === 'audio') return <AudioTranscriber downloadId={downloadId} onClose={onClose} />;
  if (kind === 'shelf_percentage') return <ShelfPercentageAnnotator downloadId={downloadId} onClose={onClose} />;
  return <CropAnnotator downloadId={downloadId} onClose={onClose} />;
}

const AUDIO_VIEWS = ['segments', 'dialogues', 'clips'] as const;
type AudioView = (typeof AUDIO_VIEWS)[number];

export default function DataPage() {
  const { t } = useT();
  usePageTitle(t('data.title'), t('data.subtitle'));
  // Раздел и подраздел живут в адресе (Алексей 18.09, AI73: «на вкладку
  // данные»): ссылку на очередь разметки надо уметь переслать, а не объяснять
  // словами, куда кликать. Состояние остаётся локальным — адрес задаёт
  // начальное и переписывается при переключении.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'hierarchy');
  // Which domain the labelling tab is showing. The two annotators label genuinely
  // different things — tables are three stages of binary heads, a shelf is one fullness
  // percentage — so this picks a tool, not a filter.
  const audioParam = searchParams.get('audio');
  const [audioView, setAudioView] = useState<AudioView>(
    audioParam && (AUDIO_VIEWS as readonly string[]).includes(audioParam)
      ? (audioParam as AudioView) : 'segments');
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      if (tab === 'pool-audio') next.set('audio', audioView);
      else next.delete('audio');
      return next;
    }, { replace: true });
  }, [tab, audioView, setSearchParams]);
  const [activeDownloadId, setActiveDownloadId] = useState<number | null>(null);

  // Restore an in-progress copy after a page refresh (React state is lost, but
  // the downloaded copy lives on the server). Auto-open ONLY when there's exactly
  // one active copy — with several (multiple people / datasets at once) we can't
  // know which was being annotated, so show the hint and let the user pick one
  // from the hierarchy's "Открыть" buttons.
  const { data: active } = useSWR(
    'mml-active-downloads',
    () => getActiveDownloads().then(r => r.data),
    { revalidateOnFocus: false },
  );
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!restoredRef.current && activeDownloadId == null && active && active.length === 1) {
      restoredRef.current = true;
      setActiveDownloadId(active[0].id);
    }
  }, [active, activeDownloadId]);

  return (
    <div className="mx-auto w-full max-w-[1900px] space-y-4 px-4 py-6">
      <Tabs value={tab} onValueChange={value => {
        setTab(value);
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('tab', value);
          next.delete('collection');
          next.delete('view');
          return next;
        });
      }}>
        {/* ONE labelling tab. The old download-a-dataset annotator is gone from the bar:
            the pooled one reads Postgres + S3 directly, so there is nothing to download
            and no reason to make anyone choose between two tools that do the same job.
            The `annotate` content stays mounted because Датасеты still routes a legacy
            per-dataset download into it — unreachable in normal use, kept so an
            in-flight download is not orphaned mid-migration. */}
        {/* Одна строка (владелец 14.09, L1): Датасеты / Разметка: Столы · Выкладка · Аудио.
            Раньше домен выбирался вторым рядом кнопок внутри вкладки «Разметка». */}
        <div className="flex flex-wrap items-center gap-3">
          <TabsList>
            <TabsTrigger value="hierarchy">{t('data.tab.hierarchy')}</TabsTrigger>
          </TabsList>
          <span className="text-sm text-muted-foreground">{t('data.tab.annotate')}:</span>
          <TabsList>
            {(['table', 'shelf', 'boxes', 'people', 'audio'] as PoolDomain[]).map(d => (
              <TabsTrigger key={d} value={`pool-${d}`}>
                {t(`data.pool.domain.${d}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {tab === 'pool-audio' && (
            // Аудио: отдельные 30-секундные клипы (транскрипт) и разговоры целиком (разметка, L2).
            <div role="tablist" className="inline-flex h-9 items-center gap-1 rounded-lg border border-border bg-card p-1 text-sm shadow-sm">
              {AUDIO_VIEWS.map(v => (
                <button key={v} type="button" role="tab" aria-selected={audioView === v} onClick={() => setAudioView(v)}
                  className={`rounded-md px-3 py-1 ${audioView === v ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                  {t(`data.audio.${v}`)}
                </button>
              ))}
            </div>
          )}
        </div>
        <TabsContent value="pool-table"><CollectionFilter domain="table" /><PooledTableAnnotator key={searchParams.get('collection') || 'all'} /></TabsContent>
        <TabsContent value="pool-shelf"><CollectionFilter domain="shelf" /><PooledShelfAnnotator key={searchParams.get('collection') || 'all'} /></TabsContent>
        {/* Боксы детектора на отменённых фото YOLO-стеллажей (Алексей 29.09, AI191). */}
        <TabsContent value="pool-boxes"><BoxAnnotator /></TabsContent>
        {/* Люди на полных кадрах — дообучение детектора людей (Алексей, Riders Lodge кам 248). */}
        <TabsContent value="pool-people"><PeopleAnnotator /></TabsContent>
        <TabsContent value="pool-audio">
          {audioView === 'dialogues' ? <ConversationMarkupTool />
            : audioView === 'segments' ? <SegmentStream /> : <PooledAudioAnnotator />}
        </TabsContent>
        <TabsContent value="hierarchy">
          <DatasetHierarchy
            onDownloadStarted={(id) => { setActiveDownloadId(id); setTab('annotate'); }}
            onOpenPool={(d: PoolDomain) => setTab(`pool-${d}`)}
          />
        </TabsContent>
        <TabsContent value="annotate">
          {activeDownloadId == null
            ? <div className="text-sm text-muted-foreground">{t('data.annotate.pickHint')}</div>
            : <AnnotatorSwitch
                downloadId={activeDownloadId}
                onClose={() => {
                  setActiveDownloadId(null);
                  setTab('hierarchy');
                  // The annotator may have removed the local copy or uploaded a
                  // new version — refresh the hierarchy + active-downloads so
                  // Датасеты doesn't show a stale "present locally" state.
                  globalMutate('mml-hierarchy');
                  globalMutate('mml-active-downloads');
                }}
              />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
